// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - CreditBadge
// Badge affichant le solde de crédits avec animation pulse
// Clique pour ouvrir le modal des abonnements
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, Zap, Loader2 } from 'lucide-react';
import CreditsModal from './CreditsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour récupérer l'userId depuis différentes sources
const getUserId = () => {
  // Essayer différentes sources de données utilisateur
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  const userId = localStorage.getItem('userId');
  
  if (userId) return userId;
  
  if (ecosystemUser) {
    try {
      const parsed = JSON.parse(ecosystemUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (buyerUser) {
    try {
      const parsed = JSON.parse(buyerUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (clientUser) {
    try {
      const parsed = JSON.parse(clientUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  return null;
};

// Helper pour vérifier si connecté
const isUserLoggedIn = () => {
  const ecosystemToken = localStorage.getItem('ecosystem_token');
  const buyerToken = localStorage.getItem('buyer_token');
  const clientToken = localStorage.getItem('client_token');
  return !!(ecosystemToken || buyerToken || clientToken);
};

export default function CreditBadge({ className = '' }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  
  const userId = getUserId();
  const isLoggedIn = isUserLoggedIn();
  
  // Récupérer le solde
  useEffect(() => {
    if (!userId || !isLoggedIn) {
      setLoading(false);
      return;
    }
    
    const fetchBalance = async () => {
      try {
        const response = await fetch(`${API_URL}/api/credits/balance/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setBalance(data);
          
          // Animation pulse si crédits bas
          if (data.total_credits < 50) {
            setShowPulse(true);
          }
        }
      } catch (err) {
        console.error('Error fetching credits:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBalance();
    
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, [userId, isLoggedIn]);
  
  // Ne pas afficher si non connecté
  if (!isLoggedIn) return null;
  
  // Formater le nombre de crédits
  const formatCredits = (num) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num?.toString() || '0';
  };
  
  // Couleur selon le niveau de crédits
  const getBadgeColor = () => {
    if (!balance) return 'bg-gray-100 text-gray-600';
    const credits = balance.total_credits || 0;
    
    if (credits === 0) return 'bg-red-100 text-red-700 border-red-200';
    if (credits < 50) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (credits < 200) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border-emerald-200';
  };
  
  return (
    <>
      <motion.button
        onClick={() => setShowModal(true)}
        className={`
          relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-full
          border text-sm font-medium cursor-pointer
          transition-all duration-200 hover:shadow-md hover:scale-105
          ${getBadgeColor()}
          ${className}
        `}
        whileTap={{ scale: 0.95 }}
        data-testid="credit-badge"
      >
        {/* Animation pulse si crédits bas */}
        <AnimatePresence>
          {showPulse && (
            <motion.div
              className="absolute inset-0 rounded-full bg-orange-400"
              initial={{ opacity: 0.5, scale: 1 }}
              animate={{ opacity: 0, scale: 1.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          )}
        </AnimatePresence>
        
        {/* Icône */}
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Coins className="w-4 h-4" />
        )}
        
        {/* Nombre de crédits */}
        <span className="font-semibold tabular-nums">
          {loading ? '...' : formatCredits(balance?.total_credits)}
        </span>
        
        {/* Badge Pro/Business si abonnement actif */}
        {balance?.subscription_plan && balance.subscription_plan !== 'free' && (
          <span className="ml-0.5 px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold rounded-full uppercase">
            {balance.subscription_plan === 'business' ? 'BIZ' : balance.subscription_plan === 'pro' ? 'PRO' : ''}
          </span>
        )}
      </motion.button>
      
      {/* Modal des crédits et abonnements */}
      <CreditsModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)}
        balance={balance}
        onRefresh={() => {
          // Rafraîchir le solde après une action
          if (userId) {
            fetch(`${API_URL}/api/credits/balance/${userId}`)
              .then(res => res.json())
              .then(data => setBalance(data))
              .catch(console.error);
          }
        }}
      />
    </>
  );
}
